import { ChangeDetectionStrategy, Component, LOCALE_ID, computed, inject, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { PasswordModule } from 'primeng/password';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { TranslationService } from '@/layout/service/translation.service';
import { GitHubSettings, errorDetail, githubTokenExpiry } from '@/cloud/models/github.models';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';

/**
 * Tenant integrations: the GitHub connection used by the package library to show and raise issues.
 * The token is validated against GitHub, stored encrypted and never shown again; only its hint and the
 * login it belongs to are displayed.
 */
@Component({
    selector: 'app-namespace-integrations',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, MessageModule, PasswordModule, TagModule, TooltipModule, ConfirmDialogModule, SectionCardComponent, EmptyStateComponent],
    providers: [ConfirmationService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './namespace-section.scss',
    template: `
        <p-confirmdialog />
        <app-section-card class="github-card" [title]="t()('github.title')" [subtitle]="t()('github.intro')" icon="pi pi-github">
            <!-- One element per @if: Angular only projects a control-flow block into [badge] when it has a single root. -->
            @if (settings()?.configured) {
                <p-tag badge severity="success" [rounded]="true" icon="pi pi-check" [value]="t()('github.connected')" />
            } @else if (settings()) {
                <p-tag badge severity="secondary" [rounded]="true" [value]="t()('github.notConnected')" />
            }
            @if (settings(); as s) {
                <div class="github-body">
                    @if (!s.encryptionAvailable) {
                        <p-message severity="warn">{{ t()('github.encryptionMissing') }}</p-message>
                    }
                    @if (s.lastRejectedUtc) {
                        <p-message severity="warn">{{ t()('github.lastRejected').replace('{date}', (s.lastRejectedUtc | date: 'medium') ?? '') }}</p-message>
                    }
                    @if (expiryWarning(); as warning) {
                        <p-message [severity]="expiry().kind === 'expired' ? 'error' : 'warn'">{{ warning }}</p-message>
                    }

                    @if (s.configured) {
                        <dl class="github-details">
                            <div>
                                <dt>{{ t()('github.account') }}</dt>
                                <dd>
                                    <a class="github-login" [href]="'https://github.com/' + (s.login ?? '')" target="_blank" rel="noopener noreferrer"
                                        ><i class="pi pi-github" aria-hidden="true"></i> {{ s.login }} <i class="pi pi-external-link" aria-hidden="true"></i
                                    ></a>
                                </dd>
                            </div>
                            <div>
                                <dt>{{ t()('github.tokenLabel') }}</dt>
                                <dd><code class="github-hint">{{ s.tokenHint }}</code></dd>
                            </div>
                            <div>
                                <dt>{{ t()('github.expiresLabel') }}</dt>
                                <dd>
                                    @switch (expiry().kind) {
                                        @case ('none') {
                                            {{ t()('github.noExpiry') }}
                                        }
                                        @case ('unknown') {
                                            <span class="github-muted">{{ t()('github.expiryUnknown') }}</span>
                                        }
                                        @default {
                                            <span [class.github-expiry--warn]="expiryUrgent()">{{ expiryDate() }}</span>
                                        }
                                    }
                                </dd>
                            </div>
                            @if (s.updatedUtc) {
                                <div>
                                    <dt>{{ t()('github.lastUpdated') }}</dt>
                                    <dd>
                                        {{ s.updatedUtc | date: 'medium' }}
                                        @if (s.updatedByName || s.updatedBy) {
                                            <span class="github-muted">· {{ s.updatedByName || s.updatedBy }}</span>
                                        }
                                    </dd>
                                </div>
                            }
                        </dl>
                    }

                    @if (canManage()) {
                        @if (!s.configured || replacing()) {
                            <div class="github-replace" [class.github-replace--inline]="s.configured">
                                <div class="ns-field">
                                    <label for="github-token">{{ t()(s.configured ? 'github.newTokenLabel' : 'github.tokenLabel') }}</label>
                                    <!-- Write-only: the saved token is never sent back, so there is nothing to reveal (#262). -->
                                    <p-password
                                        inputId="github-token"
                                        [ngModel]="token()"
                                        (ngModelChange)="token.set($event)"
                                        [feedback]="false"
                                        autocomplete="new-password"
                                        placeholder="github_pat_… / ghp_…"
                                        styleClass="w-full"
                                        inputStyleClass="w-full"
                                        [disabled]="saving() || !s.encryptionAvailable"
                                        (keydown.enter)="save()"
                                    />
                                    <small>{{ t()('github.tokenHelp') }}</small>
                                </div>
                                <div class="ns-actions">
                                    <p-button [label]="t()(s.configured ? 'github.replace' : 'github.save')" icon="pi pi-save" (onClick)="save()" [disabled]="!canSave()" [loading]="saving()" />
                                    @if (s.configured) {
                                        <p-button [label]="t()('github.cancel')" severity="secondary" [text]="true" (onClick)="cancelReplace()" [disabled]="saving()" />
                                    }
                                </div>
                            </div>
                        } @else {
                            <div class="ns-actions github-actions">
                                <p-button [label]="t()('github.replace')" icon="pi pi-refresh" [severity]="expiryUrgent() ? undefined : 'secondary'" [outlined]="!expiryUrgent()" (onClick)="startReplace()" [disabled]="!s.encryptionAvailable" />
                                <p-button [label]="t()('github.remove')" icon="pi pi-trash" severity="danger" [text]="true" (onClick)="remove()" [disabled]="removing()" [loading]="removing()" />
                            </div>
                        }
                    } @else {
                        <p class="ns-meta">{{ t()('github.readOnly') }}</p>
                    }
                </div>
            } @else if (!loading()) {
                <app-empty-state icon="pi pi-exclamation-circle" tone="error" [title]="t()('github.loadSettingsFailed')" [compact]="true" />
            }
        </app-section-card>
    `,
    styles: [
        `
            .github-card {
                max-width: 46rem;
            }
            .github-body {
                display: grid;
                gap: 1.25rem;
            }
            .github-details {
                display: grid;
                gap: 0;
                margin: 0;
                border: 1px solid var(--surface-border);
                border-radius: var(--app-card-radius);
                overflow: hidden;

                > div {
                    display: grid;
                    grid-template-columns: minmax(8rem, 11rem) minmax(0, 1fr);
                    align-items: center;
                    gap: 1rem;
                    padding: 0.7rem 1rem;
                }
                > div + div {
                    border-top: 1px solid var(--surface-border);
                }
                dt {
                    color: var(--text-color-secondary);
                    font-size: 0.875rem;
                }
                dd {
                    margin: 0;
                    min-width: 0;
                    overflow-wrap: anywhere;
                }
            }
            .github-hint {
                padding: 0.15rem 0.5rem;
                border-radius: 0.35rem;
                background: var(--app-surface-inset, var(--surface-ground));
                font-size: 0.875rem;
            }
            .github-login {
                display: inline-flex;
                align-items: center;
                gap: 0.4rem;
                color: inherit;
                font-weight: 600;
                text-decoration: none;

                .pi-external-link {
                    font-size: 0.7rem;
                    color: var(--text-color-secondary);
                }
            }
            .github-login:hover {
                text-decoration: underline;
            }
            .github-muted {
                color: var(--text-color-secondary);
            }
            .github-expiry--warn {
                color: var(--app-status-warning-fg, var(--p-orange-500));
                font-weight: 600;
            }
            .github-replace {
                display: grid;
                gap: 0.9rem;
            }
            .github-replace--inline,
            .github-actions {
                padding-top: 1.25rem;
                border-top: 1px solid var(--surface-border);
            }
            @media (max-width: 480px) {
                .github-details > div {
                    grid-template-columns: 1fr;
                    gap: 0.2rem;
                }
            }
        `
    ]
})
export class NamespaceIntegrations {
    private api = inject(CloudApiService);
    private route = inject(ActivatedRoute);
    private messageService = inject(MessageService);
    private confirmationService = inject(ConfirmationService);
    readonly t = inject(TranslationService).t;

    private namespaceId = signal('');
    settings = signal<GitHubSettings | null>(null);
    loading = signal(false);
    saving = signal(false);
    removing = signal(false);
    token = signal('');
    /** A saved token is replaced, never edited: the input only appears after "Replace token". */
    replacing = signal(false);

    readonly expiry = computed(() => githubTokenExpiry(this.settings()));
    private readonly datePipe = new DatePipe(inject(LOCALE_ID));
    /** Soon or expired: Replace token becomes the primary action. */
    readonly expiryUrgent = computed(() => this.expiry().kind === 'soon' || this.expiry().kind === 'expired');
    readonly expiryDate = computed(() => {
        const e = this.expiry();
        return 'expiresUtc' in e ? (this.datePipe.transform(e.expiresUtc, 'mediumDate') ?? '') : '';
    });
    readonly expiryWarning = computed(() => {
        const e = this.expiry();
        const t = this.t();
        if (e.kind === 'expired') return t('github.expired').replace('{date}', this.expiryDate());
        if (e.kind !== 'soon') return null;
        return e.days === 0 ? t('github.expiringToday') : t('github.expiringSoon').replace('{days}', String(e.days)).replace('{date}', this.expiryDate());
    });

    canManage = computed(() => this.api.hasCapability(this.namespaceId(), 'namespace.manage'));
    canSave = computed(() => this.token().trim().length > 0 && !this.saving() && (this.settings()?.encryptionAvailable ?? false));

    constructor() {
        this.route.parent?.paramMap.pipe(takeUntilDestroyed()).subscribe((params) => {
            this.namespaceId.set(params.get('namespaceId') ?? '');
            this.load();
        });
        // ?replace=1 (linked from the expiry banner on the Issues page) opens the replacement input straight away.
        this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe((params) => {
            if (params.get('replace') === '1') this.replacing.set(true);
        });
    }

    load(): void {
        if (!this.namespaceId()) return;
        this.loading.set(true);
        this.api
            .getNamespaceGitHubSettings(this.namespaceId())
            .then((s) => this.settings.set(s))
            .catch((err) => this.toastError(this.t()('github.loadSettingsFailed'), err))
            .finally(() => this.loading.set(false));
    }

    save(): void {
        if (!this.canSave()) return;
        this.saving.set(true);
        this.api
            .setNamespaceGitHubToken(this.namespaceId(), this.token().trim())
            .then((s) => {
                this.settings.set(s);
                this.token.set('');
                this.replacing.set(false);
                this.messageService.add({ severity: 'success', summary: this.t()('github.saved'), detail: s.login ? this.t()('github.connectedAs').replace('{login}', s.login) : undefined });
            })
            .catch((err) => this.toastError(this.t()('github.saveFailed'), err))
            .finally(() => this.saving.set(false));
    }

    startReplace(): void {
        this.token.set('');
        this.replacing.set(true);
    }

    cancelReplace(): void {
        this.token.set('');
        this.replacing.set(false);
    }

    remove(): void {
        this.confirmationService.confirm({
            header: this.t()('github.remove'),
            message: this.t()('github.removeConfirm'),
            icon: 'pi pi-exclamation-triangle',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.removing.set(true);
                this.api
                    .clearNamespaceGitHubToken(this.namespaceId())
                    .then((s) => {
                        this.settings.set(s);
                        this.messageService.add({ severity: 'success', summary: this.t()('github.removed') });
                    })
                    .catch((err) => this.toastError(this.t()('github.removeFailed'), err))
                    .finally(() => this.removing.set(false));
            }
        });
    }

    private toastError(summary: string, err: unknown): void {
        this.messageService.add({ severity: 'error', summary, detail: errorDetail(err, summary) });
    }
}
